import { IRunningOrder, IRunningOrderDogClass, IShow } from "@eagerdog/interfaces";
import { Constants } from "@eagerdog/constants";

import { classBlockMinutes } from "./judge-schedule-timeline.util";
import { sortShowsRespectingTrailingLinks } from "./judge-schedule.util";

const GAP_NAME = "Gap";

export function scheduleDayKey(value: string | Date, timeZone?: string): string {
  const date = new Date(value);
  const zone = timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: zone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
}

/** Trial 1 → T1, Show 1 → S1, Pull 1 → P1, Test 1 → Te1, Fun Day → Fun. */
export function showTrialToken(showName: string): string {
  const name = (showName || "").trim();
  const match = name.match(/^(trial|show|pull|test)\s+(\d+)$/i);
  if (match) {
    const word = match[1].toLowerCase();
    const prefix = word === "test" ? "Te" : word.charAt(0).toUpperCase();
    return `${prefix}${match[2]}`;
  }
  if (name.toLowerCase() === "fun day") {
    return "Fun";
  }
  return name;
}

export function judgeIdentityKey(name?: string, number?: string): string {
  return `${(name || "").trim()}|${(number || "").trim()}`;
}

function isGap(dogClass: IRunningOrderDogClass): boolean {
  return dogClass.type === Constants.running_order_dog_class_type.break && (dogClass.name || "").trim() === GAP_NAME;
}

function cloneShows(shows: IShow[]): IShow[] {
  return JSON.parse(JSON.stringify(shows));
}

export interface ConcurrentTile {
  showId: string;
  ring: string;
  judgeIndex: number;
  classIndex: number;
  dogClass: IRunningOrderDogClass;
  minutes: number;
  start: Date;
  end: Date;
  token: string;
}

export interface ConcurrentJudge {
  judgeName: string;
  judgeNumber?: string;
  key: string;
  start: Date;
  end: Date;
  totalMinutes: number;
  judgingMinutes: number;
  sources: { showId: string; judgeIndex: number }[];
  tiles: ConcurrentTile[];
}

export interface ConcurrentRing {
  ring: string;
  start: Date;
  end: Date;
  totalMinutes: number;
  judges: ConcurrentJudge[];
}

function addMinutes(date: Date, minutes: number): Date {
  return new Date(date.getTime() + minutes * 60 * 1000);
}

/**
 * One day's shows, already filtered. Rings stay. Judges in a ring run one
 * after another. Classes inside a judge follow day_order across trials.
 */
export function buildConcurrentDay(shows: IShow[]): ConcurrentRing[] {
  const orderedShows = sortShowsRespectingTrailingLinks(shows);
  const ringNames: string[] = [];
  orderedShows.forEach((show) => {
    (show.rings || []).forEach((ring) => {
      if (!ringNames.includes(ring.ring)) ringNames.push(ring.ring);
    });
  });

  return ringNames.map((ringName) => {
    const showsWithRing = orderedShows.filter((show) => (show.rings || []).some((ring) => ring.ring === ringName));
    const start = showsWithRing.reduce<Date>((earliest, show) => {
      const date = new Date(show.show_date);
      return date.getTime() < earliest.getTime() ? date : earliest;
    }, new Date(showsWithRing[0]?.show_date || Date.now()));

    const judgeOrder: string[] = [];
    const sources = new Map<string, { showId: string; judgeIndex: number; judgeName: string; judgeNumber?: string }[]>();
    showsWithRing.forEach((show) => {
      const ring = (show.rings || []).find((item) => item.ring === ringName);
      (ring?.running_order || []).forEach((judge, judgeIndex) => {
        const key = judgeIdentityKey(judge.judge_name, judge.judge_number);
        if (!sources.has(key)) {
          sources.set(key, []);
          judgeOrder.push(key);
        }
        sources.get(key)!.push({
          showId: show._id?.toString() || "",
          judgeIndex,
          judgeName: judge.judge_name,
          judgeNumber: judge.judge_number,
        });
      });
    });

    let cursor = start;
    const judges: ConcurrentJudge[] = judgeOrder.map((key) => {
      const judgeSources = sources.get(key) || [];
      const judgeStart = cursor;
      const tiles: ConcurrentTile[] = [];
      judgeSources.forEach((source) => {
        const show = showsWithRing.find((item) => (item._id?.toString() || "") === source.showId);
        const ring = (show?.rings || []).find((item) => item.ring === ringName);
        const judge = ring?.running_order?.[source.judgeIndex];
        (judge?.dog_classes || []).forEach((dogClass, classIndex) => {
          tiles.push({
            showId: source.showId,
            ring: ringName,
            judgeIndex: source.judgeIndex,
            classIndex,
            dogClass,
            minutes: classBlockMinutes(dogClass),
            start: judgeStart,
            end: judgeStart,
            token: showTrialToken(show?.show_name || ""),
          });
        });
      });
      tiles.sort((a, b) => {
        const aOrder = a.dogClass.day_order;
        const bOrder = b.dogClass.day_order;
        if (aOrder == null && bOrder == null) return 0;
        if (aOrder == null) return 1;
        if (bOrder == null) return -1;
        return aOrder - bOrder;
      });
      let totalMinutes = 0;
      let judgingMinutes = 0;
      tiles.forEach((tile) => {
        tile.start = cursor;
        tile.end = addMinutes(cursor, tile.minutes);
        cursor = tile.end;
        totalMinutes += tile.minutes;
        if (tile.dogClass.type !== Constants.running_order_dog_class_type.break && !tile.dogClass.group_with) {
          judgingMinutes += tile.minutes;
        }
      });
      return {
        judgeName: judgeSources[0]?.judgeName || "",
        judgeNumber: judgeSources[0]?.judgeNumber,
        key,
        start: judgeStart,
        end: cursor,
        totalMinutes,
        judgingMinutes,
        sources: judgeSources.map((source) => ({ showId: source.showId, judgeIndex: source.judgeIndex })),
        tiles,
      };
    });

    return {
      ring: ringName,
      start,
      end: cursor,
      totalMinutes: judges.reduce((sum, judge) => sum + judge.totalMinutes, 0),
      judges,
    };
  });
}

/** Fill day_order where it is missing, in today's sequential order. Existing numbers stay. */
export function assignMissingDayOrders(shows: IShow[], timeZone?: string): IShow[] {
  const next = cloneShows(shows);
  const byDay = new Map<string, IShow[]>();
  next.forEach((show) => {
    const key = scheduleDayKey(show.show_date, timeZone);
    const list = byDay.get(key) || [];
    list.push(show);
    byDay.set(key, list);
  });
  byDay.forEach((dayShows) => {
    const ordered = sortShowsRespectingTrailingLinks(dayShows);
    const ringNames = new Set<string>();
    ordered.forEach((show) => (show.rings || []).forEach((ring) => ringNames.add(ring.ring)));
    ringNames.forEach((ringName) => {
      const keys: string[] = [];
      ordered.forEach((show) => {
        const ring = (show.rings || []).find((item) => item.ring === ringName);
        (ring?.running_order || []).forEach((judge) => {
          const key = judgeIdentityKey(judge.judge_name, judge.judge_number);
          if (!keys.includes(key)) keys.push(key);
        });
      });
      keys.forEach((key) => {
        const tiles: IRunningOrderDogClass[] = [];
        ordered.forEach((show) => {
          const ring = (show.rings || []).find((item) => item.ring === ringName);
          (ring?.running_order || []).forEach((judge) => {
            if (judgeIdentityKey(judge.judge_name, judge.judge_number) !== key) return;
            (judge.dog_classes || []).forEach((dogClass) => tiles.push(dogClass));
          });
        });
        let nextOrder = tiles.reduce((max, tile) => tile.day_order != null ? Math.max(max, tile.day_order) : max, -1) + 1;
        tiles.forEach((tile) => {
          if (tile.day_order == null) tile.day_order = nextOrder++;
        });
      });
    });
  });
  return next;
}

function isGroupedChildOf(child: IRunningOrderDogClass, parent: IRunningOrderDogClass): boolean {
  if (!child.group_with || parent.group_with) return false;
  return child.group_with === `${parent.show_element}:${parent.level}`
    || (child.group_with === parent.level && child.show_element === parent.show_element);
}

function writeDayOrders(shows: IShow[], ringName: string, judgeKey: string, ordered: ConcurrentTile[], allTiles: ConcurrentTile[]): IShow[] {
  const next = cloneShows(shows);
  const orderOf = new Map<string, number>();
  let cursor = 0;
  ordered.forEach((tile) => {
    orderOf.set(`${tile.showId}:${tile.judgeIndex}:${tile.classIndex}`, cursor++);
    allTiles.forEach((child) => {
      if (child.showId === tile.showId && child.judgeIndex === tile.judgeIndex && isGroupedChildOf(child.dogClass, tile.dogClass)) {
        orderOf.set(`${child.showId}:${child.judgeIndex}:${child.classIndex}`, cursor++);
      }
    });
  });
  next.forEach((show) => {
    const showId = show._id?.toString() || "";
    const ring = (show.rings || []).find((item) => item.ring === ringName);
    (ring?.running_order || []).forEach((judge, judgeIndex) => {
      if (judgeIdentityKey(judge.judge_name, judge.judge_number) !== judgeKey) return;
      (judge.dog_classes || []).forEach((dogClass, classIndex) => {
        const value = orderOf.get(`${showId}:${judgeIndex}:${classIndex}`);
        if (value != null) dogClass.day_order = value;
      });
    });
  });
  return next;
}

/** Move a class within one judge's merged day. Returns updated shows, or undefined when nothing moved. */
export function reorderConcurrentClass(
  shows: IShow[],
  ringName: string,
  judgeName: string,
  judgeNumber: string | undefined,
  from: { showId: string; classIndex: number },
  to: { showId: string; classIndex: number }
): IShow[] | undefined {
  if (from.showId === to.showId && from.classIndex === to.classIndex) return undefined;
  const key = judgeIdentityKey(judgeName, judgeNumber);
  const day = buildConcurrentDay(shows).find((ring) => ring.ring === ringName);
  const judge = day?.judges.find((item) => item.key === key);
  if (!judge) return undefined;
  const sequence = judge.tiles.filter((tile) => !tile.dogClass.group_with);
  const fromIndex = sequence.findIndex((tile) => tile.showId === from.showId && tile.classIndex === from.classIndex);
  const toIndex = sequence.findIndex((tile) => tile.showId === to.showId && tile.classIndex === to.classIndex);
  if (fromIndex < 0 || toIndex < 0) return undefined;
  const blockStart = fromIndex > 0 && isGap(sequence[fromIndex - 1].dogClass) ? fromIndex - 1 : fromIndex;
  const block = sequence.splice(blockStart, fromIndex - blockStart + 1);
  const target = sequence.findIndex((tile) => tile.showId === to.showId && tile.classIndex === to.classIndex);
  if (target < 0) return undefined;
  sequence.splice(target, 0, ...block);
  return writeDayOrders(shows, ringName, key, sequence, judge.tiles);
}

export interface ConcurrentReassignEffects {
  scores?: {
    scoreIds?: string[];
    filter?: Record<string, unknown>;
    judge_name: string;
    judge_number: string;
  };
  element?: {
    show_element: string;
    level?: string;
    section?: string;
    judge_name: string;
    judge_number: string;
  };
}

export type ConcurrentReassignResult =
  | { ok: true; shows: IShow[]; showId: string; effects: ConcurrentReassignEffects[] }
  | { ok: false; reason: "break" | "junior" | "missing" | "ring" };

function classesAt(shows: IShow[], showId: string, ringName: string, judgeIndex: number): IRunningOrderDogClass[] | undefined {
  const show = shows.find((item) => (item._id?.toString() || "") === showId);
  return (show?.rings || []).find((item) => item.ring === ringName)?.running_order?.[judgeIndex]?.dog_classes;
}

function ensureDestJudge(show: IShow, ringName: string, judgeName: string, judgeNumber?: string): IRunningOrder | undefined {
  const ring = (show.rings || []).find((item) => item.ring === ringName);
  if (!ring) return undefined;
  if (!ring.running_order) ring.running_order = [];
  const key = judgeIdentityKey(judgeName, judgeNumber);
  let judge = ring.running_order.find((item) => judgeIdentityKey(item.judge_name, item.judge_number) === key);
  if (!judge) {
    judge = { judge_name: judgeName, judge_number: judgeNumber, dog_classes: [] };
    ring.running_order.push(judge);
  }
  if (!judge.dog_classes) judge.dog_classes = [];
  return judge;
}

function reassignEffect(
  moved: IRunningOrderDogClass,
  sourceJudgeName: string,
  destJudgeName: string,
  destJudgeNumber: string,
  showId: string
): ConcurrentReassignEffects | undefined {
  if (moved.type === Constants.running_order_dog_class_type.break) return undefined;
  const effects: ConcurrentReassignEffects = {};
  const scoreIds = moved.dog_run_order || [];
  const isSplit = moved.split_number !== undefined;
  if (scoreIds.length > 0) {
    effects.scores = { scoreIds, judge_name: destJudgeName, judge_number: destJudgeNumber };
  } else if (!moved.group_with && !isSplit && (moved.level || moved.show_element)) {
    const filter: Record<string, unknown> = { show_id: showId, judge_name: sourceJudgeName };
    if (moved.show_element) filter["dog_class.show_element"] = moved.show_element;
    if (moved.level) filter["dog_class.level"] = moved.level;
    if (moved.section) filter["dog_class.section"] = moved.section;
    effects.scores = { filter, judge_name: destJudgeName, judge_number: destJudgeNumber };
  }
  if (!moved.group_with && !isSplit && (moved.show_element || moved.level)) {
    effects.element = {
      show_element: moved.show_element || "",
      level: moved.level,
      section: moved.section,
      judge_name: destJudgeName,
      judge_number: destJudgeNumber,
    };
  }
  return effects.scores || effects.element ? effects : undefined;
}

/**
 * Move a class onto another judge. The class stays on its own trial. When that
 * judge has no assignment on the trial yet, one is added. `insertBefore` is a
 * tile in the destination judge's merged order; omit it to place the class last.
 */
export function reassignConcurrentClass(
  shows: IShow[],
  from: { showId: string; ring: string; judgeIndex: number; classIndex: number },
  dest: { ring: string; judgeKey: string },
  insertBefore?: { showId: string; classIndex: number }
): ConcurrentReassignResult {
  const days = buildConcurrentDay(shows);
  const sourceRing = days.find((ring) => ring.ring === from.ring);
  const destRing = from.ring === dest.ring ? sourceRing : days.find((ring) => ring.ring === dest.ring);
  const destJudge = destRing?.judges.find((judge) => judge.key === dest.judgeKey);
  const original = classesAt(shows, from.showId, from.ring, from.judgeIndex)?.[from.classIndex];
  const sourceOrder = (shows.find((item) => (item._id?.toString() || "") === from.showId)?.rings || [])
    .find((item) => item.ring === from.ring)?.running_order?.[from.judgeIndex];
  if (!original || !destJudge || !sourceOrder) return { ok: false, reason: "missing" };
  if (judgeIdentityKey(sourceOrder.judge_name, sourceOrder.judge_number) === dest.judgeKey && from.ring === dest.ring) {
    return { ok: false, reason: "missing" };
  }
  if (original.type === Constants.running_order_dog_class_type.break) return { ok: false, reason: "break" };
  if (original.show_element === Constants.dog_class_element.junior_showmanship) return { ok: false, reason: "junior" };

  const destSeq = destJudge.tiles.filter((tile) => !tile.dogClass.group_with);
  let insertAt = destSeq.length;
  if (insertBefore) {
    const index = destSeq.findIndex((tile) => tile.showId === insertBefore.showId && tile.classIndex === insertBefore.classIndex);
    if (index >= 0) insertAt = index;
  }

  const next = cloneShows(shows);
  const show = next.find((item) => (item._id?.toString() || "") === from.showId);
  if (!show) return { ok: false, reason: "missing" };
  const destRunning = ensureDestJudge(show, dest.ring, destJudge.judgeName, destJudge.judgeNumber);
  const sourceClasses = classesAt(next, from.showId, from.ring, from.judgeIndex);
  const parent = sourceClasses?.[from.classIndex];
  if (!destRunning || !sourceClasses || !parent || destRunning.dog_classes === sourceClasses) {
    return { ok: false, reason: destRunning ? "missing" : "ring" };
  }

  const destRefs = destSeq.map((tile) => classesAt(next, tile.showId, dest.ring, tile.judgeIndex)?.[tile.classIndex]).filter((tile): tile is IRunningOrderDogClass => !!tile);
  const dupeIndex = destRunning.dog_classes.findIndex((dogClass) =>
    dogClass.show_element === parent.show_element &&
    dogClass.level === parent.level &&
    (dogClass.entries || 0) === 0 &&
    dogClass.type !== Constants.running_order_dog_class_type.break &&
    !dogClass.group_with
  );
  let refs = destRefs;
  if (dupeIndex >= 0) {
    const [dupe] = destRunning.dog_classes.splice(dupeIndex, 1);
    const dupePos = refs.indexOf(dupe);
    if (dupePos >= 0) {
      refs = refs.filter((tile) => tile !== dupe);
      if (dupePos < insertAt) insertAt -= 1;
    }
  }

  let arrayInsert = destRunning.dog_classes.length;
  for (let index = insertAt; index < refs.length; index += 1) {
    const at = destRunning.dog_classes.indexOf(refs[index]);
    if (at >= 0) {
      arrayInsert = at;
      break;
    }
  }

  const gap = from.classIndex > 0 && isGap(sourceClasses[from.classIndex - 1]) ? sourceClasses[from.classIndex - 1] : undefined;
  const children = sourceClasses.filter((dogClass, index) => index !== from.classIndex && isGroupedChildOf(dogClass, parent));
  const block = [...(gap ? [gap] : []), parent, ...children];
  const removing = new Set(block);
  const kept = sourceClasses.filter((dogClass) => !removing.has(dogClass));
  sourceClasses.splice(0, sourceClasses.length, ...kept);
  destRunning.dog_classes.splice(arrayInsert, 0, ...block);

  const ordered = [...refs];
  ordered.splice(insertAt, 0, ...(gap ? [gap, parent] : [parent]));
  let cursor = 0;
  ordered.forEach((tile) => {
    tile.day_order = cursor++;
    next.forEach((item) => {
      const ring = (item.rings || []).find((candidate) => candidate.ring === dest.ring);
      (ring?.running_order || []).forEach((judge) => {
        if (judgeIdentityKey(judge.judge_name, judge.judge_number) !== dest.judgeKey) return;
        if (!(judge.dog_classes || []).includes(tile)) return;
        (judge.dog_classes || []).forEach((child) => {
          if (isGroupedChildOf(child, tile)) child.day_order = cursor++;
        });
      });
    });
  });

  const destNumber = destJudge.judgeNumber || "";
  const effects = block
    .map((tile) => reassignEffect(tile, sourceOrder.judge_name, destJudge.judgeName, destNumber, from.showId))
    .filter((effect): effect is ConcurrentReassignEffects => !!effect);

  return { ok: true, shows: next, showId: from.showId, effects };
}

/** Grow or shrink the gap immediately before a class in the merged order. */
export function shiftConcurrentClass(
  shows: IShow[],
  ringName: string,
  judgeName: string,
  judgeNumber: string | undefined,
  target: { showId: string; judgeIndex: number; classIndex: number },
  deltaMinutes: number
): IShow[] | undefined {
  if (!Number.isFinite(deltaMinutes) || Math.abs(deltaMinutes) < 0.25) return undefined;
  const key = judgeIdentityKey(judgeName, judgeNumber);
  const day = buildConcurrentDay(shows).find((ring) => ring.ring === ringName);
  const judge = day?.judges.find((item) => item.key === key);
  if (!judge) return undefined;
  const sequence = judge.tiles.filter((tile) => !tile.dogClass.group_with);
  const index = sequence.findIndex((tile) => tile.showId === target.showId && tile.classIndex === target.classIndex);
  if (index < 0) return undefined;
  const next = cloneShows(shows);
  const show = next.find((item) => (item._id?.toString() || "") === target.showId);
  const ring = (show?.rings || []).find((item) => item.ring === ringName);
  const classes = ring?.running_order?.[target.judgeIndex]?.dog_classes;
  if (!classes) return undefined;
  const minutes = Math.round(Math.abs(deltaMinutes) * 10) / 10;
  const previous = index > 0 ? sequence[index - 1] : undefined;

  if (deltaMinutes > 0) {
    if (previous && isGap(previous.dogClass) && previous.showId === target.showId) {
      const gap = classes[previous.classIndex];
      if (gap) {
        gap.duration = Math.round(((gap.duration || 0) + minutes) * 10) / 10;
        gap.total_minutes = gap.duration;
      }
      return next;
    }
    const current = classes[target.classIndex];
    const gapOrder = previous && current?.day_order != null && previous.dogClass.day_order != null
      ? (previous.dogClass.day_order + current.day_order) / 2
      : (current?.day_order ?? 0) - 0.5;
    classes.splice(target.classIndex, 0, {
      type: Constants.running_order_dog_class_type.break,
      duration: minutes,
      total_minutes: minutes,
      name: GAP_NAME,
      day_order: gapOrder,
    });
    return next;
  }

  if (!previous || !isGap(previous.dogClass) || previous.showId !== target.showId) return undefined;
  const gap = classes[previous.classIndex];
  if (!gap || (gap.duration || 0) + 0.05 < minutes) return undefined;
  gap.duration = Math.round(((gap.duration || 0) - minutes) * 10) / 10;
  gap.total_minutes = gap.duration;
  if ((gap.duration || 0) <= 0.05) classes.splice(previous.classIndex, 1);
  return next;
}

/**
 * In-memory copy for exports. The earliest show of the day keeps each ring's
 * merged running order. Later shows on that day keep the ring, with no
 * classes, so a per-show loop does not print them twice.
 */
export function projectConcurrentShows(shows: IShow[], timeZone?: string): IShow[] {
  const next = cloneShows(shows);
  const byDay = new Map<string, IShow[]>();
  next.forEach((show) => {
    const key = scheduleDayKey(show.show_date, timeZone);
    const list = byDay.get(key) || [];
    list.push(show);
    byDay.set(key, list);
  });
  byDay.forEach((dayShows) => {
    const rings = buildConcurrentDay(dayShows);
    const ordered = sortShowsRespectingTrailingLinks(dayShows);
    rings.forEach((ring) => {
      const host = ordered.find((show) => (show.rings || []).some((item) => item.ring === ring.ring));
      if (!host) return;
      const hostRing = (host.rings || []).find((item) => item.ring === ring.ring);
      if (!hostRing) return;
      hostRing.running_order = ring.judges.map((judge) => ({
        judge_name: judge.judgeName,
        judge_number: judge.judgeNumber,
        total_minutes: judge.totalMinutes,
        dog_classes: judge.tiles.map((tile) => ({
          ...tile.dogClass,
          concurrent_show_id: tile.showId,
          concurrent_token: tile.dogClass.type === Constants.running_order_dog_class_type.break || tile.dogClass.group_with
            ? undefined
            : tile.token,
        } as IRunningOrderDogClass)),
      })) as IRunningOrder[];
      ordered.forEach((show) => {
        if (show === host) return;
        show.rings = (show.rings || []).filter((item) => item.ring !== ring.ring);
      });
    });
  });
  return next;
}
